import { z } from "zod"
import {
  ActionTypeEnum,
  WorkflowConditionFieldEnum,
  WorkflowConditionLogicOperatorEnum,
  WorkflowConditionOperatorEnum,
  WorkflowEventEnum,
} from "../campaigns/response_data"

const WorkflowEventSchemaEnum = { ...WorkflowEventEnum } as const
const WorkflowConditionFieldSchemaEnum = { ...WorkflowConditionFieldEnum } as const

export const WorkflowNodeResponseSchema = z.object({
  id: z.bigint().transform(String),
  parentId: z.bigint().transform(String).optional().nullable(),
  branch: z.enum(["Pass", "Fail"]),
  order: z.number(),
  createdAt: z.date().transform((value) => value.toISOString()),
  updatedAt: z.date().transform((value) => value.toISOString()),
  event: z
    .object({
      type: z.enum(WorkflowEventSchemaEnum),
      config: z.any().optional().nullable(),
    })
    .nullable(),
  conditions: z.array(
    z.object({
      field: z.enum(WorkflowConditionFieldSchemaEnum),
      operator: z.enum(WorkflowConditionOperatorEnum),
      logic: z.enum(WorkflowConditionLogicOperatorEnum).nullable(),
      value: z.string().nullable(),
      config: z.record(z.string(), z.any()).optional().nullable(),
    }),
  ),
  action: z
    .object({
      type: z.enum(ActionTypeEnum),
      config: z.any().nullable(),
    })
    .nullable(),
})

export const WorkflowResponseDataSchema = z.object({
  id: z.bigint().transform(String),
  campaignId: z.bigint().transform(String).nullable(),
  name: z.string().nullable(),
  isEnabled: z.boolean(),
  createdAt: z.date().transform((value) => value.toISOString()),
  updatedAt: z.date().transform((value) => value.toISOString()),
  archivedAt: z
    .date()
    .transform((value) => value?.toISOString())
    .nullable(),
  nodes: z.array(WorkflowNodeResponseSchema),
})

export const WorkflowsResponseDataSchema = z.array(WorkflowResponseDataSchema)

// An entity (capability, goal, recipient) referenced by the imported file but absent in this
// company. The reference is stripped from the preview so the builder forces the user to re-pick it.
export const WorkflowImportWarningSchema = z.object({
  reference: z.enum(["Capability", "Goal", "Recipient"]),
})

// Import does NOT persist. The backend verifies + decodes the file and returns a preview blueprint
// for the canvas to load as an unsaved draft. Node configs here may be intentionally incomplete
// (stripped references), so this is looser than WorkflowNodeSchema — it carries no config refines.
const WorkflowImportPreviewConditionSchema = z.object({
  field: z.enum(WorkflowConditionFieldSchemaEnum),
  operator: z.enum(WorkflowConditionOperatorEnum),
  value: z.any().nullish(),
  logic: z.enum(WorkflowConditionLogicOperatorEnum).nullish(),
  config: z.record(z.string(), z.any()).nullish(),
})

const WorkflowImportPreviewNodeSchema = z
  .object({
    id: z.string(),
    parentId: z.string().nullish(),
    branch: z.enum(["Pass", "Fail"]).optional(),
    order: z.number().nullish(),
  })
  .and(
    z.discriminatedUnion("type", [
      z.object({
        type: z.literal("Event"),
        event: z.enum(WorkflowEventSchemaEnum),
        config: z.record(z.string(), z.any()).nullish(),
      }),
      z.object({
        type: z.literal("Action"),
        action: z.enum(ActionTypeEnum),
        config: z.record(z.string(), z.any()).nullish(),
      }),
      z.object({
        type: z.literal("Condition"),
        conditions: z.array(WorkflowImportPreviewConditionSchema),
      }),
    ]),
  )

export const WorkflowImportPreviewResponseDataSchema = z.object({
  blueprint: z.object({
    name: z.string().nullable(),
    nodes: z.array(WorkflowImportPreviewNodeSchema),
  }),
  warnings: z.array(WorkflowImportWarningSchema),
})

// The signed .permutflow file contents, downloaded by the client as an opaque blob.
export const WorkflowExportResponseDataSchema = z.object({
  file: z.string(),
})
